import { ExtAdapter, ExtFrameMessage, extMark, HandleFrameMessageResp, HandleType, ShowFlag } from "@hyext-inner/ext-web-comp";
import md5 from "md5";
import { BusType, EventName } from "../../../util/hy";
import { delay } from "../../../util/promise";
import { styleToSelector } from "../../../util/style";
import HyExtAdapterInLiveRoom from "../../adapters/HyExtAdapterInLiveRoom";
import HyExtEndpointModule from "../HyExtEndpointModule";

type PopupMode = 'NORMAL' | 'NOTICE'

type Manager = {
  popupMode: PopupMode
  enter?: (from: Manager, popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) => Promise<void>
  exit?: (to: Manager, popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) => Promise<void>
  reposition: (popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) => Promise<void>
  setLayout: (popupModule: HyExtWebPopupEndpointModule, req: SetLayoutReq, adapter: ExtAdapter) => Promise<void>
}

const normalManager: Manager = (() => {
  const layoutMap: {
    [key: string]: {
      x: number
      y: number
      width: number
      height: number
      visible: boolean
      alpha: number
    }
  } = {}

  const enter = async (from: Manager, popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) => {
    if (from.popupMode === 'NOTICE') {
      return setLayout(popupModule, {
        ...layoutMap[popupModule.id],
        visible: false,
      }, adapter)
    }
  }

  const setLayout = async (popupModule: HyExtWebPopupEndpointModule, req: SetLayoutReq, adapter: ExtAdapter) => {
    layoutMap[popupModule.id] = {
      ...layoutMap[popupModule.id] || {},
      ...req,
    }
    reposition(popupModule, adapter)
  }

  const reposition = async (popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) => {
    const { x, y, width, height, visible, alpha } = {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      alpha: 1,
      visible: false,
      ...(layoutMap[popupModule.id] || {}),
    }
    const refWidth = popupModule.$ref.width()
    const refHeight = popupModule.$ref.height()
    if (visible) {
      if (popupModule.extEndpoint.typeAuthority.popupContainer === 1) {
        (adapter as HyExtAdapterInLiveRoom).chatroomPanelExtSwitch({ switch: true })
        await delay(100)
      }
      const refOffset = popupModule.$ref.offset() || { top: 0, left: 0 }
      let popUpWidth = Math.round(refWidth * Math.min(1, Math.max(0, width)))
      popUpWidth -= popUpWidth % 2
      let popUpHeight = Math.round(refHeight * Math.min(1, Math.max(0, height)))
      popUpHeight -= popUpHeight % 2
      popupModule.$popup.width(popUpWidth)
      popupModule.$popup.height(popUpHeight)
      popupModule.showPanel(adapter)
      popupModule.$popup.offset({
        top: refOffset.top + refHeight * Math.min(1, Math.max(0, y)),
        left: refOffset.left + refWidth * Math.min(1, Math.max(0, x))
      })
      popupModule.$popup.css({
        opacity: Math.max(0, Math.min(1, alpha))
      })
    } else {
      popupModule.hidePanel(adapter)
    }
    return Promise.resolve()
  }

  return { popupMode: 'NORMAL' as const, setLayout, reposition, enter }
})()

const noticeManager: Manager = (() => {
  const s: HyExtWebPopupEndpointModule[] = []

  const width = 264
  const height = 83
  const left = 12
  const bottom = 32
  const gap = 12

  const show = (popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) => {
    const index = s.findIndex(m => m === popupModule)
    popupModule.$popup.css({
      left,
      top: popupModule.$ref.height() - height - bottom - (height + gap) * index,
      width,
      height,
    })
    popupModule.showPanel(adapter)
  }

  return {
    popupMode: 'NOTICE' as const,
    async enter(from: Manager, popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) {
      s.push(popupModule)
      s.forEach(m => show(m, adapter))
    },
    async exit(to: Manager, popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) {
      const index = s.findIndex(m => m === popupModule)
      if (index >= 0) {
        s.splice(index, 1)
      }
      s.forEach(m => show(m, adapter))
    },
    async setLayout(popupModule: HyExtWebPopupEndpointModule, req: SetLayoutReq, adapter: ExtAdapter) {
      return Promise.reject(new Error('提示模式下不能setLayout，请先退回普通模式'))
    },
    async reposition(popupModule: HyExtWebPopupEndpointModule, adapter: ExtAdapter) {
      show(popupModule, adapter)
    }
  }
})()

const managerMap: { [key in PopupMode]: Manager } = {
  'NORMAL': normalManager,
  'NOTICE': noticeManager,
}

export default class HyExtWebPopupEndpointModule extends HyExtEndpointModule {
  id: string = md5(String(Math.random()))
  style: any
  selector: any
  $popup: any
  $ref: any
  screenWidth: number = 0
  screenHeight: number = 0
  showed: boolean = false
  modeManager: Manager
  gesturePenetrationMouseMoveHandler: (e: MouseEvent) => void = null
  // 业务方法
  async reposition(adapter: ExtAdapter): Promise<void> {
    this.modeManager.reposition(this, adapter)
  }
  unloadPopup(adapter:ExtAdapter) {
    this.extFrame.unload(adapter)
  }
  loadPopup(adapter:ExtAdapter) {
    this.emit('postInitResolved')
    this.extFrame.load(adapter)
  }
  handleContextResize(adapter: ExtAdapter) {
    const screenWidth = this.$ref.width()
    const screenHeight = this.$ref.height()
    if (this.screenWidth !== screenWidth || this.screenHeight !== screenHeight) {
      this.bus.getBus(BusType.LAYOUT).emit(EventName.LAYOUT_CHANGE_NOTICE, {
        screenWidth,
        screenHeight,
      })
      this.reposition(adapter)
    }
    this.screenWidth = screenWidth
    this.screenHeight = screenHeight
  }
  // SDK方法
  setLayout(req: SetLayoutReq, adapter: ExtAdapter): Promise<void> {
    return this.modeManager.setLayout(this, req, adapter)
  }
  onLayoutChange(req:OnLayoutChangeReq, adapter:ExtAdapter):Promise<void> {
    const { target } = req
    this.bus.on(BusType.LAYOUT, EventName.LAYOUT_CHANGE_NOTICE, notice => {
      const { screenWidth, screenHeight } = notice
      this.extFrame.postMessageToBusiFrame(target, {
        screenWidth,
        screenHeight,
        isLandscape: true
      }, adapter)
    }, true)
    // TODO 调用onLayoutChange固定触发一次
    setTimeout(() => this.bus.getBus(BusType.LAYOUT).emit(EventName.LAYOUT_CHANGE_NOTICE, {
      screenWidth: this.$ref.width(),
      screenHeight: this.$ref.height(),
    }), 100)
    return Promise.resolve()
  }
  // 覆盖方法
  startup(adapter:ExtAdapter) {
    if (this.hbTimer === 0) {
      this.sys_pageshow_ext_panelshow(adapter)
    }
    super.startup(adapter)
  }
  // 抽象方法
  async initEndpoint(adapter: ExtAdapter): Promise<void> {
    const hyExtAdapter = adapter as HyExtAdapterInLiveRoom
    this.style = require('@/sass/popup.scss')
    this.selector = styleToSelector(this.style)
    this.$popup = $(require('@/tpl/popup.html')({
      style: this.style
    }))
    this.$popup.css({ 'z-index': this.extEndpoint.typeAuthority.weight })
    if (this.extEndpoint.extConfig) {
      let { width, height } = this.extEndpoint.typeAuthority
      if (!isNaN(width) && width) {
        width = width - width % 2
        this.$popup.css({ width })
      }
      if (!isNaN(height) && height) {
        height = height - height % 2
        this.$popup.css({ height })
      }
    }
    extMark('load_supframe_start', this.extMain.extUuid, this.extEndpoint.extType)
    this.$popup.find('section').append(this.extFrame.iframe)
    if (this.extEndpoint.typeAuthority) {
      switch (this.extEndpoint.typeAuthority.popupContainer) {
        case 1:
          $('#wrap-ext').prepend(this.$popup)
          this.$ref = $('#chatRoom')
          break
        case 2: {
          let $container
          if (this.extEndpoint.typeAuthority.popupLevel < 300) {
            $container = $(hyExtAdapter.getExtContainerUnderBarrage())
          } else {
            $container = $(hyExtAdapter.getExtPopupContainer())
          }
          $container.prepend(this.$popup)
          const player = await hyExtAdapter.getPlayer()
          this.$ref = player.getVideoContainer()
          break
        }
        default:
          this.$ref = $('html')
          $('body').append(this.$popup)
          break
      }
    } else {
      this.$ref = $('html')
      $('body').append(this.$popup)
    }
    // 监听容器大小变化
    if (this.extEndpoint.typeAuthority && this.extEndpoint.typeAuthority.popupContainer === 2) {
      hyExtAdapter.onPageFullScreen(() => this.handleContextResize(adapter))
    }
    $(window).on('resize', () => this.handleContextResize(adapter))

    this.modeManager = normalManager
    this.reposition(adapter)
    this.sys_pageshow_ext_list(adapter)
    this.sys_startup_ext_interactive(adapter)
    return Promise.resolve()
  }
  statusNotice(extTypeIconInfo:MidExtQuery.ExtTypeIconInfo, adapter:ExtAdapter):Promise<void> {
    if (extTypeIconInfo) {
      this.extEndpoint.typeAuthority.extTypeIconInfo = extTypeIconInfo
    }
    const { showFlag } = this.extEndpoint.typeAuthority.extTypeIconInfo
    if (showFlag & Number(ShowFlag.PANEL)) {
      this.loadPopup(adapter)
    } else {
      // TODO
      this.$popup.hide()
      this.unloadPopup(adapter)
    }
    return Promise.resolve()
  }
  postInit() {
    const { showFlag } = this.extEndpoint.typeAuthority.extTypeIconInfo
    if (showFlag & Number(ShowFlag.PANEL)) {
      this.loaded = true
      return Promise.resolve()
    } else {
      return new Promise<void>(resolve => {
        this.once('postInitResolved', () => resolve())
      })
    }
  }
  showEndpointPanel(adapter:ExtAdapter): Promise<void> {
    // 第一次show的时候才触发onLoad的生命周期
    if (!this.showed) {
      this.showed = true
      this.postLifeLoad(adapter)
      this.postLifeAppear(adapter)
    } else {
      if (!this.$popup.is(':visible')) {
        this.postLifeEnterForeground(adapter)
        this.postLifeAppear(adapter)
      }
    }
    this.$popup.show()
    return Promise.resolve()
  }
  hideEndpointPanel(adapter:ExtAdapter): Promise<void> {
    if (this.$popup.is(':visible')) {
      this.postLifeLeaveForeground(adapter)
      this.postLifeDisappear(adapter)
    }
    this.$popup.hide()
    return Promise.resolve()
  }
  showEndpointEntrance(): Promise<void> {
    return Promise.resolve()
  }
  hideEndpointEntrance(): Promise<void> {
    return Promise.resolve()
  }
  ready(): Promise<boolean> {
    return Promise.resolve(true)
  }
  showSupError(): Promise<void> {
    return Promise.resolve()
  }
  getModalContainer(): Promise<any> {
    return Promise.resolve($('body'))
  }
  textNotice(tips: string): Promise<void> {
    throw new Error('Method not implemented.');
  }
  close(content: string): Promise<void> {
    this.logger.warn('显示下线提示', content)
    this.extFrame.destroy()
    return Promise.resolve()
  }
  async setMode(req: SetModeReq, adapter: ExtAdapter): Promise<void> {
    if (req.mode !== this.modeManager.popupMode) {
      const prevModeManager = this.modeManager
      this.modeManager = managerMap[req.mode]
      if (prevModeManager.exit) await prevModeManager.exit(this.modeManager, this, adapter)
      if (this.modeManager.enter) await this.modeManager.enter(prevModeManager, this, adapter)
    }
  }
  async handleFrameMessage(msg: ExtFrameMessage, adapter: ExtAdapter): Promise<HandleFrameMessageResp> {
    if (msg.originData.event === 'setPointerEvents') {
      if (this.gesturePenetrationMouseMoveHandler) {
        const ev = msg.originData.through ? "none" : "auto";
        const pointerEvents = this.$popup.css('pointer-events')
        if (pointerEvents !== ev) {
          this.$popup.css('pointer-events', ev)
          this.extFrame.iframe.style.pointerEvents = ev;
        }
      }
      return {
        handleType: HandleType.SILENT,
        rsp: null,
        err: null,
      }
    } else {
      return super.handleFrameMessage(msg, adapter)
    }
  }
  async setGesturePenetration(req: SetGesturePenetrationReq, adapter: ExtAdapter): Promise<void> {
    if (req.enable) {
      this.gesturePenetrationMouseMoveHandler = e => {
        if (this.extFrame.busiFrameLoaded) {
          let coord = {
            x: e.pageX - this.extFrame.iframe.getBoundingClientRect().left,
            y: e.pageY - this.extFrame.iframe.getBoundingClientRect().top,
          };
          // 判断当前鼠标是否在iframe中
          // 一旦鼠标进入iframe中，判断target元素是否要响应，iframe的pointerEvents设置为auto，否则iframe接收不到事件
          // 父级页面捕获不到任何事件
          this.extFrame.iframe.contentWindow.postMessage(
            {
              event: "onMouseMoveOnParent",
              x: coord.x,
              y: coord.y,
            },
            "*"
          );
        }
      }
      document.addEventListener("mousemove", this.gesturePenetrationMouseMoveHandler, true)
    } else {
      if (this.gesturePenetrationMouseMoveHandler) {
        document.removeEventListener("mousemove", this.gesturePenetrationMouseMoveHandler, true)
        this.gesturePenetrationMouseMoveHandler = null
      }
      this.$popup.css('pointer-events', 'auto')
      this.extFrame.iframe.style.pointerEvents = 'auto';
    }
  }
}